import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { format } from "date-fns";
import { FaExchangeAlt, FaCalendarAlt, FaTable, FaFileInvoice, FaUser } from "react-icons/fa";

import CardComponent from "../../components/cards/CardComponent";
import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import { TextfieldwithLabel } from "../../components/Buttons/Textfield";
import { SubmitButton } from "../../components/Buttons/Textfield";
import { getHeaders } from "../../utility/getHeader";
import { getUserName } from "../../functions/storageUtils";
import fetchData from "../../functions/fetchData";

export default function MoveBillToEntertainment() {
  const currentDate = format(new Date(), "yyyy-MM-dd");
  const [userName, setUserName] = useState("");
  const [entertainmentBills, setEntertainmentBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastCloseDate, setLastCloseDate] = useState(currentDate);

  const [formData, setFormData] = useState({
    userId: "",
    userName: "",
    tableNo: "",
    reason: "",
    billNo: "",
  });

  const [dateRange, setDateRange] = useState({
    startDate: currentDate,
    endDate: currentDate,
  });

  // Columns for the entertainment bills table
  const columns = [
    { label: "Invoice No", field: "inv" },
    { label: "Table No", field: "tables" },
    { label: "Moved By", field: "userid" },
    { label: "Reason", field: "remark" },
    { label: "Moved Date", field: "kayote" },
    { label: "Bill Amount", field: "net_amount" },
    { label: "Mode", field: "mode" },
  ];

  // Get logged-in user information on component mount
  useEffect(() => {
    const fetchedUserName = getUserName();
    setUserName(fetchedUserName);
    setFormData((prev) => ({
      ...prev,
      userName: fetchedUserName,
    }));
    
    // Fetch last close date
    const fetchLastCloseDate = async () => {
      try {
        const response = await axios.get('/last-close-date', getHeaders());
        if (response.data && response.data.closeDate) {
          setLastCloseDate(response.data.closeDate);
        }
      } catch (error) {
        console.error("Error fetching last close date:", error);
        // Keep using currentDate as fallback
      }
    };
    
    fetchLastCloseDate();
  }, []);

  // Fetch entertainment bills based on date range
  const fetchEntertainmentBills = async () => {
    setLoading(true);
    try {
      // Fetch from final_bill table where mode='entertainment'
      const response = await fetchData("final_bill", null, "id", {flag: '6'});
      
      // Filter by date range using setup_dte
      const data = response?.data || response || [];
      const entertainmentData = Array.isArray(data) ? data.filter(bill => {
        if (!bill.setup_dte) return false;
        try {
          const billDate = new Date(bill.setup_dte);
          // Check if date is valid
          if (isNaN(billDate.getTime())) return false;
          const formattedBillDate = billDate.toISOString().split('T')[0];
          return formattedBillDate >= dateRange.startDate && formattedBillDate <= dateRange.endDate;
        } catch (e) {
          console.warn("Invalid date for bill:", bill.setup_dte);
          return false;
        }
      }) : [];
      
      setEntertainmentBills(entertainmentData);
      
      if (entertainmentData.length > 0) {
        toast.success(`Loaded ${entertainmentData.length} entertainment bill(s)`);
      }
    } catch (error) {
      console.error("Error fetching entertainment bills:", error);
      toast.error("Failed to load entertainment bills");
      setEntertainmentBills([]);
    } finally {
      setLoading(false);
    }
  };

  // Load entertainment bills on component mount and when date range changes
  useEffect(() => {
    if (lastCloseDate) {
      fetchEntertainmentBills();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate, lastCloseDate]);

  // Fetch bill details when bill number changes with debounce
  useEffect(() => {
    if (!formData.billNo || formData.billNo.trim() === "") {
      setFormData(prev => ({ ...prev, tableNo: "" }));
      return;
    }

    const delayDebounce = setTimeout(() => {
      fetchBillDetails(formData.billNo);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(delayDebounce);
  }, [formData.billNo]);

  // Fetch bill details when bill number is entered
  const fetchBillDetails = async (billNo) => {
    try {
      console.log("Fetching bill details for invoice:", billNo);
      const response = await fetchData("final_bill", null, "id", {inv: billNo});

      console.log("Bill details response:", response.data);

      if (response.data && response.data.length > 0) {
        const billData = response.data[0];
        console.log("Setting table number:", billData.tables);
        setFormData(prev => ({
          ...prev,
          tableNo: billData.tables || ""
        }));
      } else {
        console.log("No bill found");
        setFormData(prev => ({ ...prev, tableNo: "" }));
      }
    } catch (error) {
      console.error("Error fetching bill details:", error);
      setFormData(prev => ({ ...prev, tableNo: "" }));
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle date range changes
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prevRange) => ({
      ...prevRange,
      [name]: value,
    }));
  };

  // Handle bill movement to entertainment
  const handleMoveBillToEntertainment = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.billNo) {
      toast.error("Please enter Bill Number");
      return;
    }
    if (!formData.reason) {
      toast.error("Please provide a reason for moving to entertainment");
      return;
    }

    try {
      const invoiceNo = formData.billNo;
      const currentTime = format(new Date(), "yyyy-MM-dd HH:mm:ss");
      let updatedBarCart = false;
      let updatedFoodCart = false;
      let deletedFromCollection = false;
      
      // Step 1: Update bar_cart
      try {
        const barCartUpdateData = {
          mode: 'Entertainment',
          userid: formData.userName || userName
        };
        
        const barCartResponse = await axios.put(
          `/updatecommondata/bar_cart/invoice/${invoiceNo}`,
          barCartUpdateData,
          getHeaders()
        );
        
        if (barCartResponse.data.affectedRows > 0) {
          updatedBarCart = true;
          console.log("Updated bar_cart");
        }
      } catch (error) {
        console.log("No records in bar_cart or error:", error.message);
      }

      // Step 2: Update food_cart
      try {
        const foodCartUpdateData = {
          mode: 'Entertainment',
          userid: formData.userName || userName
        };
        
        const foodCartResponse = await axios.put(
          `/updatecommondata/food_cart/invoice/${invoiceNo}`,
          foodCartUpdateData,
          getHeaders()
        );
        
        if (foodCartResponse.data.affectedRows > 0) {
          updatedFoodCart = true;
          console.log("Updated food_cart");
        }
      } catch (error) {
        console.log("No records in food_cart or error:", error.message);
      }

      // Step 3: Delete from collection table
      try {
        const collectionResponse = await axios.delete(
          `/deletebyid/collection/barbill/${invoiceNo}`,
          getHeaders()
        );
        
        if (collectionResponse.data.success && collectionResponse.data.affectedRows > 0) {
          deletedFromCollection = true;
          console.log("Deleted from collection");
        }
      } catch (error) {
        console.log("No records in collection or error:", error.message);
      }

      // Step 4: Update final_bill table
      try {
        const updateData = {
          mode: 'Entertainment',
          kayote: currentTime,
          userid: formData.userName || userName,
          remark: formData.reason,
          paid_status: 0,
          flag: 6
        };

        const updateResponse = await axios.put(
          `/updatecommondata/final_bill/inv/${invoiceNo}`,
          updateData,
          getHeaders()
        );

        if (updateResponse.data.affectedRows > 0) {
          const updatedItems = [];
          if (updatedBarCart) updatedItems.push("bar_cart");
          if (updatedFoodCart) updatedItems.push("food_cart");
          if (deletedFromCollection) updatedItems.push("collection deleted");
          
          const message = updatedItems.length > 0 
            ? `Bill moved to entertainment successfully! Updated: ${updatedItems.join(", ")}`
            : "Bill moved to entertainment successfully!";
          
          toast.success(message);
        } else {
          toast.error("Bill not found in final_bill table");
          return;
        }
      } catch (updateError) {
        console.error("Error updating final_bill:", updateError);
        toast.error(`Could not move bill to entertainment: ${updateError.response?.data?.message || updateError.message}`);
        return;
      }
      
      // Reset form
      setFormData({
        userId: "",
        userName: userName,
        tableNo: "",
        reason: "",
        billNo: "",
      });

      // Refresh the entertainment bills list
      fetchEntertainmentBills();
    } catch (error) {
      console.error("Error moving bill to entertainment:", error);
      toast.error(
        error.response?.data?.message || "Failed to move bill to entertainment"
      );
    }
  };

  return (
    <>
      <Layout>
        <Header title="Move Bill to Entertainment" />
        <ToastContainer />
        
        <div className="row">
          {/* Move Bill Form */}
          <div className="col-12">
            <CardComponent
              title="Move Bill to Entertainment"
              headerColor="#28a745"
              pull="left"
              bodyClass="panel-body"
            >
              <form onSubmit={handleMoveBillToEntertainment}>
                <div className="panel panel-default card-view">
                  <div className="row">
                    {/* User Name */}
                    <div className="col-md-3 col-sm-6">
                      <div className="form-group">
                        <label className="control-label mb-2">
                          <FaUser className="me-2" />
                          User Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="userName"
                          value={formData.userName || userName}
                          readOnly
                          style={{ backgroundColor: "#f0f0f0" }}
                        />
                      </div>
                    </div>

                    {/* Bill Number */}
                    <div className="col-md-3 col-sm-6">
                      <TextfieldwithLabel
                        id="billNo"
                        onChange={handleInputChange}
                        value={formData.billNo}
                        type="text"
                        name="billNo"
                        lable="Bill No."
                        placeholder="Enter bill number"
                      />
                    </div>

                    {/* Reason */}
                    <div className="col-md-4 col-sm-8">
                      <div className="form-group">
                        <label className="control-label mb-2">
                          Reason to Move
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="reason"
                          value={formData.reason}
                          onChange={handleInputChange}
                          placeholder="Enter reason for moving to entertainment"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="col-md-2 col-sm-4">
                      <label className="control-label mb-2" style={{ visibility: "hidden" }}>
                        Submit
                      </label>
                      <button
                        type="submit"
                        className="btn btn-block"
                        style={{
                          background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          padding: "10px 20px",
                          fontSize: "14px",
                          fontWeight: "600",
                          boxShadow: "0 4px 12px rgba(40, 167, 69, 0.3)",
                          transition: "all 0.3s ease",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          cursor: "pointer"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow = "0 6px 16px rgba(40, 167, 69, 0.4)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "0 4px 12px rgba(40, 167, 69, 0.3)";
                        }}
                      >
                        <FaExchangeAlt style={{ fontSize: "16px" }} />
                        <span>Move </span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </CardComponent>
          </div>

          {/* Entertainment Bills Records */}
          <div className="col-12">
            <CardComponent
              title="Entertainment Bills History"
              headerColor="#17a2b8"
              pull="left"
              bodyClass="panel-body"
            >
              {/* Date Range Filter */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">
                    <FaCalendarAlt className="me-2" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="startDate"
                    value={dateRange.startDate}
                    onChange={handleDateChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    <FaCalendarAlt className="me-2" />
                    End Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="endDate"
                    value={dateRange.endDate}
                    onChange={handleDateChange}
                  />
                </div>
              </div>

              {/* Entertainment Bills Table */}
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                ) : entertainmentBills.length > 0 ? (
                  <table className="table table-striped table-bordered table-hover">
                    <thead style={{ backgroundColor: "#e3f2fd" }}>
                      <tr>
                        {columns.map((col, index) => (
                          <th key={index} style={{ color: "#1976d2", fontWeight: "600" }}>{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {entertainmentBills.map((bill, index) => (
                        <tr key={index}>
                          {columns.map((col, colIndex) => {
                            // Handle date field with validation
                            if (col.field === 'kayote' && bill[col.field]) {
                              try {
                                const date = new Date(bill[col.field]);
                                if (!isNaN(date.getTime())) {
                                  return <td key={colIndex}>{format(date, 'dd/MM/yyyy HH:mm')}</td>;
                                }
                              } catch (e) {
                                console.warn("Invalid date:", bill[col.field]);
                              }
                            }
                            return <td key={colIndex}>{bill[col.field] || '-'}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="alert alert-info text-center">
                    <FaFileInvoice className="me-2" size={20} />
                    No entertainment bills found for the selected date range
                  </div>
                )}
              </div>
            </CardComponent>
          </div>
        </div>
      </Layout>
    </>
  );
}
